import {
  AnchoredPanelType,
  CheckBox,
  FieldSet,
  FormattedString,
  InfoBlockLayout,
  InfoBlockStatus,
  InputFormSidePanel,
  InputFormSidePanelProps,
  SimpleBuilder,
  TextInput,
  TextInputTypes,
  uniqueGUID,
} from "o4a-react";
import * as React from "react";
import { Label, mergeStyleSets } from "@fluentui/react";
import * as Messages from "../../codegen/Messages";
import { exaPluggableDatabaseUrl } from "../../constants/docConstants";
import {
  alphaNumericUnderscoreRegex,
  pdbNameMaxLength,
  startWithLetterRegex,
  validatePdbPassword,
  validateTagKey,
} from "../../helpers/validationHelper";

export const enum Fields {
  PdbName = "pdbName",
  Password = "password",
  Tags = "tags",
  PasswordConfirm = "passwordConfirm",
  UnlockAdminAccount = "unlockAdminAccount",
  TdeWalletPassword = "tdeWalletPassword",
}

export interface ExaDbPdbCreatePanelProps extends
  Pick<InputFormSidePanelProps, "componentRef" | "onClose" | "onSubmit" | "title"> {
  targetId: string;
  showTags?: boolean;
}

const classNames = mergeStyleSets({
  nameInput: { width: "50%", minWidth: "107px" },
  valueInput: { width: "50%", minWidth: "107px" },
});

export const ExaDbPdbCreatePanel = ({
  targetId,
  title,
  showTags,
  componentRef,
  onSubmit,
  onClose,
}: ExaDbPdbCreatePanelProps): JSX.Element => {
  const [passwordVal, setPasswordVal] = React.useState<string>();
  const [passwordConfirmVal, setPasswordConfirmVal] = React.useState<string>();
  const [unlockAccountVal, setUnlockAccountVal] = React.useState(false);
  const [key, setKey] = React.useState<string>(uniqueGUID());

  const info = {
    messageType: InfoBlockStatus.INFO,
    message: Messages.hints.tdeWalletPasswordInfo(),
    infoLayout: InfoBlockLayout.Compact,

  };

  const onChangeUnlockAccountVal = (value: boolean): void => {
    setUnlockAccountVal(value);
  };

  const onChangePassword = (value: string): void => {
    setPasswordVal(value);
    if (passwordConfirmVal !== undefined) {
      setPasswordConfirmVal(undefined);
      setKey(uniqueGUID());
    }
  };

  return (
    <InputFormSidePanel
      componentRef={componentRef}
      anchorId={targetId}
      type={AnchoredPanelType.MEDIUM}
      title={title}
      onSubmit={onSubmit}
      onClose={onClose}
    >
      <TextInput
        required
        fieldName={Fields.PdbName}
        label={Messages.labels.pluggableDatabaseName()}
        tooltip={
          FormattedString(
            { inputText: Messages.hints.tooltipPluggableDatabase(exaPluggableDatabaseUrl) },
          ) as unknown as string
        }
        validator={(value: string) => {
          const errors: string [] = [];
          if (value && !startWithLetterRegex.test(value)) {
            errors.push(Messages.validation.nameStartChar());
          }
          if (value?.length > pdbNameMaxLength) {
            errors.push(Messages.validation.valueMaxLen(pdbNameMaxLength.toString()));
          }
          if (value && !alphaNumericUnderscoreRegex.test(value)) {
            errors.push(Messages.validation.valueAlphaNumericUnderscore());
          }
          return errors.length > 0 ? errors : undefined;
        }}
      />
      <CheckBox
        defaultValue={unlockAccountVal}
        fieldName={Fields.UnlockAdminAccount}
        label={Messages.labels.unlockMyPDBAdminAccount()}
        tooltip={Messages.hints.unlockMyPDBAdminAccount()}
        onChange={value => {
          onChangeUnlockAccountVal(value);
        }}
      />
      {unlockAccountVal && (
        <>
          <TextInput
            required
            defaultValue={passwordVal}
            fieldName={Fields.Password}
            textType={TextInputTypes.PASSWORD}
            label={Messages.labels.pdbAdminPassword()}
            onChange={value => { onChangePassword(value); }}
            validator={validatePdbPassword}
          />
          <TextInput
            required
            key={key}
            defaultValue={passwordConfirmVal}
            fieldName={Fields.PasswordConfirm}
            label={Messages.labels.pdbAdminPasswordConfirm()}
            textType={TextInputTypes.PASSWORD}
            onChange={value => { setPasswordConfirmVal(value); }}
            validator={(value: string) => {
              if (value && (value !== passwordVal)) {
                return [Messages.validation.passwordConfirmation()];
              }
              return undefined;
            }}
          />
        </>
      )}
      <TextInput
        required
        fieldName={Fields.TdeWalletPassword}
        textType={TextInputTypes.PASSWORD}
        label={Messages.labels.tdeWalletPassword()}
        statusInfo={info}
      />
      {showTags && (
        <FieldSet
          header={Messages.labels.tags()}
        >
          <SimpleBuilder fieldName={Fields.Tags}>
            <TextInput
              label={Messages.common.name()}
              fieldName="name"
              className={classNames.nameInput}
              validator={validateTagKey}
            />
            <Label>:</Label>
            <TextInput
              label={Messages.common.value()}
              fieldName="value"
              className={classNames.valueInput}
            />
          </SimpleBuilder>
        </FieldSet>
      )}
    </InputFormSidePanel>
  );
};
